import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from './api/http';
import {
  Form,
  Input,
  InputNumber,
  DatePicker,
  Row,
  Col,
  Button,
  Modal,
  message,
  Card,
  Select,
  Typography,
} from 'antd';
import { ArrowRightOutlined, CheckCircleOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import RecordAttachments from './components/RecordAttachments';
import { ExternalSubmittedFormRecord, PrintExternalSubmittedFormSummary } from './components/ExternalSubmittedFormRecord';
import PrintRepairForm from './pages/PrintRepairForm.jsx';
import { useAuth } from './auth/AuthProvider';
import { resolveActiveCustomerContext } from './auth/customerContext';
import { getRecordReturnTarget } from './recordReturnNavigation';
import { DashboardPageShell, DashboardPanel, DashboardPrimaryActionButton } from './components/dashboard';
import './RepairForm.css';

export default function RepairForm() {
  const { session } = useAuth();
  const [form] = Form.useForm();
  const [externalForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { unitId: unitFromPath } = useParams();
  const [qs] = useSearchParams();
  const unitFromQuery = qs.get('unitId') || '';
  const recordId = qs.get('id');
  const draftId = qs.get('draftId');
  const unitIdPrefill = unitFromPath || unitFromQuery || '';
  const recordReturnTarget = useMemo(
    () => getRecordReturnTarget(location, session?.role),
    [location, session?.role]
  );

  const [lift, setLift] = useState(null);
  const [baseHour, setBaseHour] = useState(0);
  const [hmTouched, setHmTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const pendingValsRef = useRef(null);
  const statusModeRef = useRef('SUBMIT');
  const inFlightRef = useRef(false);
  const [customerName, setCustomerName] = useState('');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [performedByName, setPerformedByName] = useState('');
  const [savedRecordId, setSavedRecordId] = useState(null);
  const attachmentsRef = useRef(null);
  const externalAttachmentsRef = useRef(null);
  const [formPath, setFormPath] = useState(recordId || draftId ? 'native' : null);
  const [submissionSource, setSubmissionSource] = useState('');
  const [repairRecordDetail, setRepairRecordDetail] = useState(null);

  const [isDirty, setIsDirty] = useState(false);
  const dirtyInitRef = useRef(false);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const pendingNavRef = useRef(null);

  const storedSession = (() => {
    try {
      return JSON.parse(localStorage.getItem('session') || '{}');
    } catch {
      return {};
    }
  })();

  const customerId =
    Number(localStorage.getItem('selectedCustomerId')) ||
    Number(localStorage.getItem('customerId')) ||
    Number(session?.customerId) ||
    Number(storedSession?.customerId) ||
    null;

  const technicianUserId =
    Number(session?.userId) || Number(storedSession?.userId) || Number(localStorage.getItem('userId')) || null;

  const storedCustomerName = resolveActiveCustomerContext(session).customerName;

  useEffect(() => {
    try {
      localStorage.removeItem('dirtyForm');
    } catch {}
    setIsDirty(false);
    return () => {
      try {
        localStorage.removeItem('dirtyForm');
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!recordId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/repairs/${recordId}`);
        if (!alive) return;

        setPerformedByName(data.technicianName || '');
        setSubmissionSource(data.submissionSource || 'MANAGELIFT_FORM');

        let forkliftData = null;
        if (data.unitId) {
          try {
            const res = await api.get(
              `/api/forklifts/${encodeURIComponent(data.unitId)}`
            );
            forkliftData = res.data || null;
          } catch {
            forkliftData = null;
          }
        }

        setLift(forkliftData);

        const nameFromForklift =
          data.customerName ||
          forkliftData?.customerName ||
          forkliftData?.customer?.name ||
          storedCustomerName ||
          '';
        const headerName = nameFromForklift || customerName || '';

        setCustomerName(headerName);
        setRepairRecordDetail({
          ...data,
          forklift: forkliftData,
          customerName: headerName,
        });

        const hmFromRepair = Number(data.hourMeter);
        const hmFromForklift = Number(forkliftData?.hourMeter);
        const effectiveBaseHour = Number.isFinite(hmFromRepair)
          ? hmFromRepair
          : Number.isFinite(hmFromForklift)
          ? hmFromForklift
          : 0;

        setBaseHour(effectiveBaseHour);

        form.setFieldsValue({
          customerName: headerName,
          customerAddress:
            data.address || forkliftData?.customerAddress || forkliftData?.customer?.address || '',
          customerCity:
            data.city || forkliftData?.customerCity || forkliftData?.customer?.city || '',
          customerState:
            data.state || forkliftData?.customerState || forkliftData?.customer?.state || '',
          po: data.po || '',
          dateStarted: data.dateStarted ? dayjs(data.dateStarted) : null,
          dateCompleted: data.dateCompleted ? dayjs(data.dateCompleted) : null,
          unitId: data.unitId || '',
          make: forkliftData?.make || '',
          model: forkliftData?.model || '',
          serialNumber: forkliftData?.serialNumber || '',
          hourMeter:
            data.hourMeter ??
            (Number.isFinite(hmFromForklift) ? hmFromForklift : ''),
          notes: data.notes || '',
          signature: data.technicianSignature || '',
        });

        setHmTouched(false);
        setIsReadOnly(true);
        setIsDirty(false);
        try {
          localStorage.removeItem('dirtyForm');
        } catch {}
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, [recordId, form, storedCustomerName, customerName]);

  useEffect(() => {
    if (recordId) return;
    if (!draftId) return;

    const idNum = Number(draftId);
    if (!Number.isFinite(idNum)) return;

    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/repairs/drafts/${idNum}`);
        if (!alive) return;

        dirtyInitRef.current = true;
        try {
          localStorage.removeItem('dirtyForm');
        } catch {}
        setIsDirty(false);

        form.setFieldsValue({
          po: data.po || '',
          dateStarted: data.dateStarted ? dayjs(data.dateStarted) : null,
          dateCompleted: data.dateCompleted ? dayjs(data.dateCompleted) : null,
          unitId: data.unitId || unitIdPrefill || '',
          hourMeter: data.hourMeter ?? '',
          notes: data.notes || '',
          signature: data.technicianSignature || '',
        });

        setIsReadOnly(false);
        setPerformedByName('');
        if (
          data.hourMeter !== undefined &&
          data.hourMeter !== null &&
          String(data.hourMeter) !== ''
        ) {
          setHmTouched(true);
        }
      } catch {}
    })();

    return () => {
      alive = false;
    };
  }, [draftId, recordId, form, unitIdPrefill]);

  useEffect(() => {
    if (formPath !== 'external') return;
    externalForm.setFieldsValue({
      customerName,
      unitId: unitIdPrefill,
      date: externalForm.getFieldValue('date') || dayjs(),
      hourMeter: externalForm.getFieldValue('hourMeter') ?? baseHour,
      status: externalForm.getFieldValue('status') || 'IN_SERVICE',
    });
  }, [formPath, externalForm, customerName, unitIdPrefill, baseHour]);

  useEffect(() => {
    if (recordId) return;
    if (!unitIdPrefill) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/api/forklifts/${encodeURIComponent(unitIdPrefill)}`
        );
        if (!alive) return;
        setLift(data || null);

        const hm = Number.isFinite(data?.hourMeter) ? Number(data.hourMeter) : 0;
        setBaseHour(hm);

        const name =
          data?.customerName || data?.customer?.name || storedCustomerName || '';
        setCustomerName(name);

        const baseFields = {
          customerName: name,
          customerAddress: data?.customerAddress || data?.customer?.address || '',
          customerCity: data?.customerCity || data?.customer?.city || '',
          customerState: data?.customerState || data?.customer?.state || '',
          unitId: unitIdPrefill,
          make: data?.make || '',
          model: data?.model || '',
          serialNumber: data?.serialNumber || '',
        };

        if (draftId) {
          form.setFieldsValue(baseFields);
        } else {
          form.setFieldsValue({
            ...baseFields,
            po: '',
            dateStarted: dayjs(),
            dateCompleted: dayjs(),
            hourMeter: hm,
          });
          setHmTouched(false);
        }
      } catch {
        setLift(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [recordId, unitIdPrefill, form, storedCustomerName, draftId]);

    const requestLeave = useCallback(
      (action) => {
        if (!isDirty) {
          action();
          return;
        }
        pendingNavRef.current = action;
        setUnsavedOpen(true);
      },
      [isDirty]
    );

    const handleCancel = () => {
      requestLeave(() => navigate(recordReturnTarget, { replace: true }));
    };


    const handleExternalSubmit = async (vals) => {
      if (saving || inFlightRef.current) return;
      const externalFiles = externalAttachmentsRef.current?.getPendingFiles?.('EXTERNAL_SUBMITTED_FORM') || [];
      if (externalFiles.length < 1 || externalFiles.length > 3) {
        message.error('Upload at least one and no more than three external submitted form files/pages before submitting.');
        return;
      }
      inFlightRef.current = true;
      setSaving(true);
      try {
        const payload = {
          customerId,
          technicianUserId,
          unitId: String(unitIdPrefill || vals.unitId || '').trim(),
          dateStarted: vals.date ? dayjs(vals.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
          dateCompleted: vals.date ? dayjs(vals.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
          po: vals.po?.trim() || '',
          technicianSignature: vals.signature?.trim() || '',
          notes: vals.notes?.trim() || '',
          hourMeter: Number(vals.hourMeter),
          status: vals.status,
          submissionSource: 'EXTERNAL_FORM',
        };
        const fd = new FormData();
        fd.append('payload', JSON.stringify(payload));
        externalFiles.forEach((file) => fd.append('files', file));
        const resp = await api.post('/api/repairs/external-form', fd);
        const createdId = resp?.data?.id || null;
        setSavedRecordId(createdId);
        if (createdId) {
          const photoResult = await externalAttachmentsRef.current?.flushUploads?.(createdId, 'PHOTO');
          if (photoResult && !photoResult.ok) {
            message.error(photoResult.message || 'External repair form was saved, but photo attachments failed to upload.');
            return;
          }
        }
        message.success('External repair form saved');
        navigate(recordReturnTarget, { replace: true });
      } catch (e) {
        message.error(e?.response?.data?.message || e?.message || 'Failed to save external repair form.');
      } finally {
        inFlightRef.current = false;
        setSaving(false);
      }
    };

        const openStatusDialog = (vals, mode) => {
          if (saving) return;
          pendingValsRef.current = vals;
          statusModeRef.current = mode || 'SUBMIT';

          setStatusOpen(true);
        };

    const submitWithStatus = async (status) => {
      const vals = pendingValsRef.current;
      if (!vals) return;
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      setStatusOpen(false);
      setSaving(true);

      try {
        const draftIdNum =
          draftId != null &&
          String(draftId).trim() !== '' &&
          Number.isFinite(Number(draftId))
            ? Number(draftId)
            : null;

        const payload = {
          ...(Number.isFinite(draftIdNum) ? { id: draftIdNum } : {}),
          customerId,
          technicianUserId,
          unitId: String(unitIdPrefill || vals.unitId || '').trim(),
          dateStarted: vals.dateStarted
            ? dayjs(vals.dateStarted).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD'),
          dateCompleted: vals.dateCompleted
            ? dayjs(vals.dateCompleted).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD'),
          po: vals.po?.trim() || '',
          technicianSignature: vals.signature?.trim() || '',
          notes: vals.notes?.trim() || '',
          hourMeter: Number(vals.hourMeter),
          status,
        };

        const resp = await api.post('/api/repairs', payload);
        const createdId = resp?.data?.id || null;
        setSavedRecordId(createdId);
        try {
          localStorage.removeItem('dirtyForm');
        } catch {}
        setIsDirty(false);
        message.success('Repair saved');
        if (createdId && attachmentsRef.current?.hasPending?.()) {
          const up = await attachmentsRef.current.flushUploads(createdId);
          if (!up.ok) {
            Modal.warning({ title: "Record saved", content: "Record was saved, but one or more uploads failed. Retry from the saved record if allowed by the upload panel." });
          }
        }
        navigate(recordReturnTarget, { replace: true });
      } catch {
        message.error('Failed to save repair');
      } finally {
        inFlightRef.current = false;
        setSaving(false);
      }
    };

    const saveDraftWithStatus = async (status) => {
      if (!customerId || !technicianUserId) {
        message.error('Missing session info. Please re-login.');
        setStatusOpen(false);
        return;
      }

      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const vals = pendingValsRef.current || (form.getFieldsValue(true) || {});

      setStatusOpen(false);
      setSaving(true);

      try {
        const rawHour = vals.hourMeter;
        const hourMeter = Number.isFinite(Number(rawHour)) ? Number(rawHour) : baseHour;

        const draftIdNum =
          draftId != null &&
          String(draftId).trim() !== '' &&
          Number.isFinite(Number(draftId))
            ? Number(draftId)
            : null;

        const payload = {
          ...(Number.isFinite(draftIdNum) ? { id: draftIdNum } : {}),
          customerId,
          technicianUserId,
          unitId: String(unitIdPrefill || vals.unitId || '').trim(),
          dateStarted: vals.dateStarted
            ? dayjs(vals.dateStarted).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD'),
          dateCompleted: vals.dateCompleted
            ? dayjs(vals.dateCompleted).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD'),
          po: vals.po?.trim() || '',
          technicianSignature: vals.signature?.trim() || '',
          notes: vals.notes?.trim() || '',
          hourMeter,
          status,
          workOrderStatus: 'DRAFT',
        };

        await api.post('/api/repairs/drafts', payload);

        try {
          localStorage.removeItem('dirtyForm');
        } catch {}
        setIsDirty(false);
        message.success('Draft saved');
        navigate(recordReturnTarget, { replace: true });
      } catch {
        message.error('Failed to save draft');
      } finally {
        inFlightRef.current = false;
        setSaving(false);
      }
    };

    const handlePickStatus = (status) => {
      if (saving) return;
      if (statusModeRef.current === 'DRAFT') {
        saveDraftWithStatus(status);
        return;
      }
      submitWithStatus(status);
    };

    const onFinishLater = () => {
      if (saving) return;
      if (!customerId || !technicianUserId) {
        message.error('Missing session info. Please re-login.');
        return;
      }
      const vals = form.getFieldsValue(true) || {};
      openStatusDialog(vals, 'DRAFT');
    };

    const onFinish = (vals) => {
      if (saving) return;
      if (!customerId || !technicianUserId) {
        message.error('Missing session info. Please re-login.');
        return;
      }
      openStatusDialog(vals, 'SUBMIT');
    };

    const emptyVals = useMemo(() => ({}), []);
    const allVals = Form.useWatch([], form) || emptyVals;

    useEffect(() => {
      if (isReadOnly) return;
      if (!allVals || Object.keys(allVals).length === 0) return;
      if (!dirtyInitRef.current) {
        dirtyInitRef.current = true;
        return;
      }
      setIsDirty(true);
      try {
        localStorage.setItem('dirtyForm', '1');
      } catch {}
    }, [allVals, isReadOnly]);

    useEffect(() => {
      const handleBeforeUnload = (e) => {
        if (!isDirty) return;
        e.preventDefault();
        e.returnValue = '';
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    useEffect(() => {
      const handler = (e) => {
        if (!isDirty) return;
        const sider = e.target.closest('.ant-layout-sider');
        if (!sider) return;
        const menuItem = e.target.closest('.ant-menu-item');
        if (!menuItem) return;
        const link = menuItem.querySelector('a[href]');
        if (!link) return;

        e.preventDefault();
        e.stopPropagation();

        const href = link.getAttribute('href');
        pendingNavRef.current = () => navigate(href);
        setUnsavedOpen(true);
      };

      document.addEventListener('click', handler, true);
      return () => document.removeEventListener('click', handler, true);
    }, [isDirty, navigate]);

    useEffect(() => {
      if (!isDirty) return;

      const currentUrl = window.location.href;

      const handlePopState = () => {
        window.history.pushState(null, '', currentUrl);
        requestLeave(() => {
          window.removeEventListener('popstate', handlePopState);
          navigate(-2);
        });
      };

      window.history.pushState(null, '', currentUrl);
      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }, [isDirty, navigate, requestLeave]);


  const renderPathChoice = () => (
    <DashboardPageShell
      title={customerName || resolveActiveCustomerContext(session).customerName || 'Repair Form'}
      productDashboardShell="customer"
      headerClassName="dashboard-fleet-header"
      contentClassName="dashboard-fleet-page repair-choice-page"
    >
      <DashboardPanel className="repair-choice-panel">
        <div className="repair-choice-heading">
          <Typography.Title level={3}>Submit repair record</Typography.Title>
          <Typography.Paragraph className="repair-choice-helper">
            Choose how to record repair work for this unit.
          </Typography.Paragraph>
        </div>

        <div className="repair-choice-options" aria-label="Repair record submission options">
          <article className="repair-choice-card repair-choice-card-featured">
            <div className="repair-choice-card-copy">
              <span className="repair-choice-icon repair-choice-icon-primary" aria-hidden="true"><CheckCircleOutlined /></span>
              <div>
                <Typography.Text className="repair-choice-card-eyebrow">Recommended</Typography.Text>
                <Typography.Title level={3}>Use ManageLift form</Typography.Title>
                <Typography.Paragraph>
                  Record repair work, notes, hour meter, technician signature, and status directly in ManageLift.
                </Typography.Paragraph>
              </div>
            </div>
            <DashboardPrimaryActionButton size="large" onClick={() => setFormPath('native')} className="repair-choice-button repair-choice-primary">
              Start repair form <ArrowRightOutlined />
            </DashboardPrimaryActionButton>
          </article>

          <article className="repair-choice-card">
            <div className="repair-choice-card-copy">
              <span className="repair-choice-icon" aria-hidden="true"><UploadOutlined /></span>
              <div>
                <Typography.Text className="repair-choice-card-eyebrow">External form</Typography.Text>
                <Typography.Title level={3}>Use my own form</Typography.Title>
                <Typography.Paragraph>
                  Upload a completed external repair form and keep it with this unit’s history.
                </Typography.Paragraph>
              </div>
            </div>
            <Button size="large" onClick={() => setFormPath('external')} className="repair-choice-button repair-choice-secondary">
              Submit external form <ArrowRightOutlined />
            </Button>
          </article>
        </div>

        <div className="repair-choice-footer">
          <Typography.Text className="repair-choice-footer-text">Not ready to submit? Return to records.</Typography.Text>
          <Button size="large" onClick={handleCancel} className="repair-choice-cancel">Cancel</Button>
        </div>
      </DashboardPanel>
    </DashboardPageShell>
  );

  const renderExternalForm = () => (
    <DashboardPageShell
      title={customerName || resolveActiveCustomerContext(session).customerName || 'Repair Form'}
      productDashboardShell="customer"
      headerClassName="dashboard-fleet-header"
      contentClassName="dashboard-fleet-page repair-external-page"
      contentInnerClassName="repair-external-shell"
    >
      <DashboardPanel className="repair-external-panel">
        <div className="repair-external-heading">
          <Typography.Title level={3}>Submit external repair form</Typography.Title>
          <Typography.Paragraph type="secondary" className="repair-external-helper">
            Upload the completed repair form, then add optional lift or work photos.
          </Typography.Paragraph>
        </div>
        <Card title="External repair details" className="repair-external-card">
          <Form form={externalForm} layout="vertical" onFinish={handleExternalSubmit} initialValues={{ unitId: unitIdPrefill, customerName, date: dayjs(), hourMeter: baseHour, status: 'IN_SERVICE' }} className="repair-external-form">
            <Row gutter={16}>
              <Col xs={24} md={12}><Form.Item label="Customer" name="customerName"><Input disabled /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item label="Unit ID" name="unitId" rules={[{ required: true, message: 'Unit ID required' }]}><Input disabled /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item label="Date" name="date" rules={[{ required: true, message: 'Date required' }]}><DatePicker className="repair-full-width-control" allowClear={false} disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item label="Hour Meter" name="hourMeter" rules={[{ required: true, message: 'Hour meter required' }]}><InputNumber className="repair-full-width-control" min={baseHour} /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item label="Forklift Status" name="status" rules={[{ required: true, message: 'Status required' }]}><Select options={[{ value: 'IN_SERVICE', label: 'In service' }, { value: 'DOWN', label: 'Down' }, { value: 'WAITING_ON_PARTS', label: 'Waiting on parts' }]} /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item label="Customer PO" name="po"><Input /></Form.Item></Col>
              <Col xs={24}><Form.Item label="Notes / Summary" name="notes"><Input.TextArea rows={4} /></Form.Item></Col>
              <Col xs={24}>
                <div className="repair-external-signature-row">
                  <Form.Item label="Technician Signature" name="signature" rules={[{ required: true, message: 'Signature required' }]} className="repair-external-signature-item"><Input /></Form.Item>
                </div>
              </Col>
            </Row>
            <div className="repair-external-attachments"><RecordAttachments ref={externalAttachmentsRef} recordType="REPAIR" recordId={savedRecordId} readOnly={false} attachmentTypes={['EXTERNAL_SUBMITTED_FORM', 'PHOTO']} variant="uploadSection" /></div>
            <div className="repair-external-actions"><DashboardPrimaryActionButton htmlType="submit" loading={saving}>Submit external form</DashboardPrimaryActionButton><Button onClick={handleCancel} disabled={saving}>Cancel</Button></div>
          </Form>
        </Card>
      </DashboardPanel>
    </DashboardPageShell>
  );

  if (!isReadOnly && !recordId && !draftId && !formPath) return renderPathChoice();
  if (!isReadOnly && !recordId && !draftId && formPath === 'external') return renderExternalForm();

  const isExternalFormRecord = isReadOnly && submissionSource === 'EXTERNAL_FORM';
  const formatMetadataDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '');
  const repairDetail = repairRecordDetail || {};
  const repairForklift = repairDetail.forklift || {};
  const repairMetadata = [
    { label: 'Customer', value: isExternalFormRecord ? repairDetail.customerName || customerName || '' : allVals.customerName || customerName || '' },
    { label: 'Customer PO', value: isExternalFormRecord ? repairDetail.po || '' : allVals.po || '' },
    { label: 'Unit ID', value: isExternalFormRecord ? repairDetail.unitId || unitIdPrefill || '' : allVals.unitId || unitIdPrefill || '' },
    { label: 'Make', value: isExternalFormRecord ? repairForklift.make || '' : allVals.make || '' },
    { label: 'Model', value: isExternalFormRecord ? repairForklift.model || '' : allVals.model || '' },
    { label: 'Serial Number', value: isExternalFormRecord ? repairForklift.serialNumber || '' : allVals.serialNumber || '' },
    { label: 'Date Started', value: formatMetadataDate(isExternalFormRecord ? repairDetail.dateStarted : allVals.dateStarted) },
    { label: 'Date Completed', value: formatMetadataDate(isExternalFormRecord ? repairDetail.dateCompleted : allVals.dateCompleted) },
    { label: 'Hour Meter', value: isExternalFormRecord ? repairDetail.hourMeter ?? '' : allVals.hourMeter ?? '' },
    { label: 'Notes / Summary', value: isExternalFormRecord ? repairDetail.notes || '' : allVals.notes || '' },
    { label: 'Technician Signature', value: isExternalFormRecord ? repairDetail.technicianSignature || '' : allVals.signature || '' },
  ];

  return (
    <>
      <div className="screen-only">
        <DashboardPageShell
        title={customerName || resolveActiveCustomerContext(session).customerName || 'Repair Form'}
        productDashboardShell="customer"
        headerClassName="dashboard-fleet-header"
        contentClassName="dashboard-fleet-page repair-form-page"
        contentInnerClassName="repair-form-shell"
      >
        <DashboardPanel className={`repair-form-panel${isReadOnly ? ' repair-readonly-panel' : ''}`}>
            <div className="repair-form-heading">
              <div>
                <Typography.Title level={3}>{isReadOnly ? 'Submitted Repair Record' : 'Repair Form'}</Typography.Title>
                <Typography.Paragraph className="repair-form-helper">
                  {isReadOnly ? 'Review the submitted repair record and related attachments.' : 'Record repair work, hour meter, notes, technician signature, and status for this unit.'}
                </Typography.Paragraph>
              </div>
              {performedByName && <div className="repair-performed-by">Performed By: {performedByName || '—'}</div>}
            </div>

            {isExternalFormRecord ? (
              <ExternalSubmittedFormRecord
                formName="Repair Form"
                recordType="REPAIR"
                recordId={recordId || savedRecordId}
                metadata={repairMetadata}
                performedByLabel="Performed by"
                performedByName={performedByName}
                onPrint={() => window.print()}
                onCancel={handleCancel}
              />
            ) : (
            <>
            <Form
              form={form}
              layout="vertical"
              className="repair-native-form"
              onFinish={isReadOnly ? undefined : onFinish}
              initialValues={{
                unitId: unitIdPrefill || '',
                dateStarted: dayjs(),
                dateCompleted: dayjs(),
              }}
              disabled={isReadOnly}
            >
              <Card
                className="section-card"
                title="Equipment & Customer Details"

              >
                <Row gutter={12}>
                  <Col xs={24} md={6}>
                    <Form.Item label="Customer Name" name="customerName">
                      <Input disabled placeholder="—" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Address" name="customerAddress">
                      <Input disabled placeholder="—" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="City" name="customerCity">
                      <Input disabled placeholder="—" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="State" name="customerState">
                      <Input disabled placeholder="—" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={6}>
                    <Form.Item label="Customer PO" name="po">
                      <Input
                        placeholder="PO number (optional)"

                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Date Started" name="dateStarted">
                      <DatePicker
                        className="repair-full-width-control"
                        allowClear={false}
                        disabled={isReadOnly}
                        disabledDate={(current) =>
                          current && !current.isSame(dayjs(), 'day')
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={6}>
                    <Form.Item label="Date Completed" name="dateCompleted">
                      <DatePicker
                        className="repair-full-width-control"
                        allowClear={false}
                        disabled={isReadOnly}
                        disabledDate={(current) =>
                          current && !current.isSame(dayjs(), 'day')
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Unit ID#" name="unitId">
                      <Input
                        disabled
                        placeholder={unitIdPrefill || '—'}

                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={6}>
                    <Form.Item label="Make" name="make">
                      <Input
                        disabled
                        placeholder={lift ? '' : '—'}

                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Model" name="model">
                      <Input
                        disabled
                        placeholder={lift ? '' : '—'}

                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Serial Number" name="serialNumber">
                      <Input
                        disabled
                        placeholder={lift ? '' : '—'}

                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item
                      label={`Hour Meter (current: ${baseHour})`}
                      name="hourMeter"
                      rules={[
                        { required: true, message: 'Hour meter is required' },
                        () => ({
                          validator: (_, value) => {
                            if (!hmTouched && !isReadOnly) {
                              return Promise.reject(
                                new Error(
                                  'Confirm the hour meter (enter the same or a higher value)'
                                )
                              );
                            }
                            const num = Number(value);
                            if (!Number.isFinite(num)) {
                              return Promise.reject(new Error('Enter a valid number'));
                            }
                            if (!isReadOnly && num < baseHour) {
                              return Promise.reject(
                                new Error(
                                  `Hour meter must be greater than or equal to ${baseHour}`
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        min={0}
                        className="repair-full-width-control"
                        onChange={() => setHmTouched(true)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Card className="section-card repair-work-card" title="Work Performed & Technician Signature">
                <Form.Item label="Work Performed" name="notes">
                  <Input.TextArea rows={6}  />
                </Form.Item>

                <div className="repair-signature-wrap">
                  <div className="repair-signature-row">
                    <span className="repair-signature-label">
                      <strong>* Technician Signature:</strong>
                    </span>
                    <Form.Item
                      name="signature"
                      rules={[{ required: true, message: 'Signature is required' }]}
                      className="repair-signature-item"
                    >
                      <Input />
                    </Form.Item>
                    <div />
                  </div>
                </div>

                <div className="repair-form-actions">
                  {!isReadOnly && (
                    <>
                      <DashboardPrimaryActionButton
                        htmlType="submit"
                        loading={saving}
                        disabled={saving}
                      >
                        Submit
                      </DashboardPrimaryActionButton>
                      <Button
                        type="primary"

                        disabled={saving}
                        onClick={onFinishLater}
                      >
                        Finish later
                      </Button>
                    </>
                  )}
                  {!isReadOnly && (
                    <Button
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </Card>
            </Form>
            {isReadOnly ? (
              <>
                <Card className="section-card repair-attachments-card" title="Attachments">
                  <div className="repair-readonly-attachments">
                    <Typography.Title level={4}>Photos / Attachments</Typography.Title>
                    <RecordAttachments ref={attachmentsRef} recordType="REPAIR" recordId={recordId || savedRecordId} readOnly attachmentTypes={['PHOTO']} variant="compactReadOnlySection" onUploadFailed={() => Modal.warning({ title: 'Record saved', content: 'Record was saved, but one or more uploads failed during submission. Uploads can only be added during the active submission flow.' })} />
                  </div>
                </Card>
                <div className="repair-form-actions repair-readonly-actions">
                  <DashboardPrimaryActionButton onClick={() => window.print()} disabled={saving}>Print</DashboardPrimaryActionButton>
                  <Button onClick={handleCancel} disabled={saving}>Cancel</Button>
                </div>
              </>
            ) : (
              <div className="repair-native-attachments">
                <RecordAttachments ref={attachmentsRef} recordType="REPAIR" recordId={recordId || savedRecordId} readOnly={false} attachmentTypes={['PHOTO']} onUploadFailed={() => Modal.warning({ title: 'Record saved', content: 'Record was saved, but one or more uploads failed during submission. Uploads can only be added during the active submission flow.' })} />
              </div>
            )}
            </>
            )}
        </DashboardPanel>

        <Modal
          className="repair-status-modal"
          open={statusOpen}
          onCancel={() => {
            if (saving) return;
            setStatusOpen(false);
          }}
          footer={null}
          title="Update Forklift Status"
          destroyOnHidden
          maskClosable={!saving}
          closable={!saving}
        >
          <div className="repair-modal-actions">
            <Button
              type="primary"
              onClick={() => handlePickStatus('IN_SERVICE')}
              disabled={saving}

            >
              In Service
            </Button>
            <Button
              onClick={() => handlePickStatus('DOWN')}
              disabled={saving}

            >
              Down
            </Button>
            <Button
              onClick={() => handlePickStatus('WAITING_ON_PARTS')}
              disabled={saving}

            >
              Down — Waiting on Parts
            </Button>
            <div className="repair-modal-spacer" />
            <Button
              onClick={() => setStatusOpen(false)}
              disabled={saving}

            >
              Cancel
            </Button>
          </div>
        </Modal>

        <Modal
          className="repair-unsaved-modal"
          open={unsavedOpen}
          title="Unsaved Changes"
          onCancel={() => {
            setUnsavedOpen(false);
            pendingNavRef.current = null;
          }}
          footer={[
            <Button
              key="stay"
              type="primary"

              onClick={() => {
                setUnsavedOpen(false);
                pendingNavRef.current = null;
              }}
            >
              Stay on Page
            </Button>,
            <Button
              key="leave"
              type="primary"
              danger

              onClick={() => {
                const action = pendingNavRef.current;
                setUnsavedOpen(false);
                pendingNavRef.current = null;
                try {
                  localStorage.removeItem('dirtyForm');
                } catch {}
                setIsDirty(false);
                if (action) action();
              }}
            >
              Leave Page
            </Button>,
          ]}
          centered
          maskClosable={false}
        >
          You have unsaved changes on this form. If you leave this page, your entries will not be saved.
        </Modal>
        </DashboardPageShell>
      </div>

      <div className="print-only" aria-hidden="true">
        {isExternalFormRecord ? (
          <PrintExternalSubmittedFormSummary
            formName="Repair Form"
            metadata={repairMetadata}
            performedByLabel="Performed by"
            performedByName={performedByName}
          />
        ) : (
        <PrintRepairForm
          header={{
            customerName: allVals.customerName || customerName || '',
            address: allVals.customerAddress || '',
            city: allVals.customerCity || '',
            state: allVals.customerState || '',
            dateStarted: allVals.dateStarted
              ? dayjs(allVals.dateStarted).format('YYYY-MM-DD')
              : '',
            dateCompleted: allVals.dateCompleted
              ? dayjs(allVals.dateCompleted).format('YYYY-MM-DD')
              : '',
            po: allVals.po || '',
            unitId: allVals.unitId || unitIdPrefill || '',
            make: allVals.make || '',
            model: allVals.model || '',
            serialNumber: allVals.serialNumber || '',
            hourMeter: allVals.hourMeter ?? '',
            notes: allVals.notes || '',
            technicianSignature: allVals.signature || '',
            performedByName: performedByName || '',
            recordId: recordId || savedRecordId || '',
            submittedAt: repairRecordDetail?.createdAt || repairRecordDetail?.submittedAt || '',
          }}
        />
        )}
      </div>
    </>
  );
}
